import {StudyPhase} from 'graphql-client/__generated/graphqlGlobalTypes';
import {MAX_PROCEDURE_NAME_LENGTH} from 'procedures/design/ProcedureDesignConstants';

export const STUDY_NAME_MAX_LENGTH = 255; // Keep in-sync with benchling/studies/domain/constants.py:STUDY_NAME_MAX_LENGTH
export const STUDY_DESCRIPTION_MAX_LENGTH = 2048; // Keep in-sync with benchling/studies/domain/constants.py:STUDY_DESCRIPTION_MAX_LENGTH
export const STUDY_DISPLAY_ID_MAX_LENGTH = 255; // Keep in-sync with benchling/studies/domain/constants.py:STUDY_DISPLAY_ID_MAX_LENGTH
export const EMPTY_PREFIX = ''; // Keep in-sync with benchling/studies/domain/constants.py:EMPTY_PREFIX

/**
 * Returns true when a study schema is configured for manual ID assignment (no prefix).
 *
 * The GraphQL API returns null for the prefix field when manual ID mode is set, but
 * internally we use "" (EMPTY_PREFIX) when constructing update payloads. This helper
 * normalizes both representations so callers don't need to know which form they have.
 */
export function isManualIdMode(prefix: string | null | undefined): boolean {
  return !prefix;
}

/**
 * Procedure field length constants must stay in sync with ProcedureValidationConstants
 * in benchling/procedures/constants/validation_constants.py.
 */
export const EXPERIMENT_VALIDATION_CONSTANTS = {
  PROCEDURE_RUN_NAME_MAX_LENGTH: 255,
  CONDITION_NAME_MAX_LENGTH: 255,
  CONDITION_DESCRIPTION_MAX_LENGTH: 255,
  MAX_REPLICATES_PER_CONDITION: 500,
  MAX_CONDITIONS_PER_PLAN: 100,
};

/**
 * Holds the number of assay runs rendered on clinical based study overview page.
 */
export const ACCESSIBLE_ASSAY_RUN_COUNT_ON_OVERVIEW_PAGE = 25;

/**
 * Constants for clinical study validation. These values should always be kept in sync with constants
 * in benchling/studies/domain/constants.py
 */
export const CLINICAL_STUDIES_VALIDATION_CONSTANTS = {
  /* Assay runs */
  MAX_NUM_ASSAY_RUNS_PER_CLINICAL_BASED_STUDY: 25000,
  MAX_NUM_ASSAY_RUNS_PER_MODAL_RUN: 500,
  /* Sample definitions */
  MAX_NUM_SAMPLE_DEFINITIONS_PER_DESIGN: 600,
  SAMPLE_DEFINITION_LABEL_MAX_LENGTH: 255,
  SAMPLE_DEFINITION_VISIT_MAX_LENGTH: 255,
  /* Sample test plans */
  MAX_NUM_SAMPLE_TEST_PLANS_PER_DESIGN: 3000,
  /* Treatment groups */
  MAX_NUM_TREATMENT_GROUPS_PER_DESIGN: 25,
  MIN_TARGET_NUMBER_OF_SUBJECTS: 0,
  TREATMENT_GROUP_NAME_MAX_LENGTH: 255,
  TREATMENT_GROUP_LABEL_MAX_LENGTH: 255,
  TREATMENT_GROUP_DESCRIPTION_MAX_LENGTH: 255,
};

export const EXPERIMENT_NAME_MAX_LENGTH = MAX_PROCEDURE_NAME_LENGTH; // use for validation of experiment name
export const AUTOGENERATED_NAME_TEXT = '(Autogenerated)';
export const STUDY_FILTER_ANY = 'ANY';
export const STUDY_PHASES = Object.values(StudyPhase);
